import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  listExpenses,
  listExpenseCategories,
  listExpenseReceipts,
  createExpense,
  deleteExpense,
  createBatchExpenses,
  deleteExpenseReceipt,
  createExpenseCategory,
  deleteExpenseCategory,
  computeKdv,
  type CreateExpenseInput,
  type CreateBatchExpenseInput,
  type BatchExpenseItem,
  type ExpenseRecord,
  type ExpenseCategory,
  type ExpenseReceiptRecord,
} from '../../services/accounting';
import { listRestaurants, type RestaurantRecord } from '../../services/restaurants';

const currency = (v: number) => `₺${v.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;

const todayStr = () => new Date().toISOString().slice(0, 10);

type ViewTab = 'all' | 'receipts';

const EMPTY_LINE: BatchExpenseItem = { categoryId: '', description: '', amount: 0, kdvRate: 0 };

export default function ExpensesManager() {
  const qc = useQueryClient();

  // View tab
  const [viewTab, setViewTab] = useState<ViewTab>('all');

  // Filters
  const [filterRestaurant, setFilterRestaurant] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  // Single-item form
  const [showForm, setShowForm] = useState(false);
  const [formRestaurant, setFormRestaurant] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formDate, setFormDate] = useState(todayStr());
  const [formNote, setFormNote] = useState('');
  const [formKdvRate, setFormKdvRate] = useState('0');

  // Batch form
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [batchRestaurant, setBatchRestaurant] = useState('');
  const [batchDate, setBatchDate] = useState(todayStr());
  const [batchSupplier, setBatchSupplier] = useState('');
  const [batchNote, setBatchNote] = useState('');
  const [batchLines, setBatchLines] = useState<BatchExpenseItem[]>([{ ...EMPTY_LINE }]);

  // Category form
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatDesc, setNewCatDesc] = useState('');

  // Expanded receipt
  const [expandedReceipt, setExpandedReceipt] = useState<string | null>(null);

  // Data
  const { data: restaurants = [] } = useQuery<RestaurantRecord[]>({
    queryKey: ['restaurants'],
    queryFn: listRestaurants,
  });

  const { data: categories = [] } = useQuery<ExpenseCategory[]>({
    queryKey: ['accounting', 'expense-categories'],
    queryFn: listExpenseCategories,
  });

  const {
    data: expenses = [],
    isLoading,
    error,
  } = useQuery<ExpenseRecord[]>({
    queryKey: ['accounting', 'expenses', filterRestaurant, filterDateFrom, filterDateTo],
    queryFn: () =>
      listExpenses(
        filterRestaurant || undefined,
        filterDateFrom || undefined,
        filterDateTo || undefined,
      ),
  });

  const {
    data: receipts = [],
    isLoading: receiptsLoading,
  } = useQuery<ExpenseReceiptRecord[]>({
    queryKey: ['accounting', 'expense-receipts', filterRestaurant, filterDateFrom, filterDateTo],
    queryFn: () =>
      listExpenseReceipts(
        filterRestaurant || undefined,
        filterDateFrom || undefined,
        filterDateTo || undefined,
      ),
  });

  // Client-side filtering (category + text search)
  const filtered = useMemo(() => {
    let result = expenses;
    if (filterCategory) result = result.filter((e) => e.categoryId === filterCategory);
    if (filterSearch.trim()) {
      const q = filterSearch.trim().toLowerCase();
      result = result.filter((e) => e.description.toLowerCase().includes(q));
    }
    return result;
  }, [expenses, filterCategory, filterSearch]);

  const totalFiltered = useMemo(
    () => filtered.reduce((s, e) => s + e.amount, 0),
    [filtered],
  );

  const totalKdvFiltered = useMemo(
    () => filtered.reduce((s, e) => s + e.kdvAmount, 0),
    [filtered],
  );

  // Mutations
  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: ['accounting', 'expenses'] });
    void qc.invalidateQueries({ queryKey: ['accounting', 'expense-receipts'] });
  };

  const addMutation = useMutation({
    mutationFn: (input: CreateExpenseInput) => createExpense(input),
    onSuccess: () => {
      invalidateAll();
      setShowForm(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteExpense(id),
    onSuccess: invalidateAll,
  });

  const batchMutation = useMutation({
    mutationFn: (input: CreateBatchExpenseInput) => createBatchExpenses(input),
    onSuccess: () => {
      invalidateAll();
      setShowBatchForm(false);
      resetBatchForm();
    },
  });

  const deleteReceiptMutation = useMutation({
    mutationFn: (id: string) => deleteExpenseReceipt(id),
    onSuccess: invalidateAll,
  });

  const addCategoryMutation = useMutation({
    mutationFn: ({ name, description }: { name: string; description?: string }) =>
      createExpenseCategory(name, description),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['accounting', 'expense-categories'] });
      setNewCatName('');
      setNewCatDesc('');
      setShowCategoryForm(false);
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => deleteExpenseCategory(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['accounting', 'expense-categories'] });
    },
  });

  function resetForm() {
    setFormDescription('');
    setFormAmount('');
    setFormDate(todayStr());
    setFormNote('');
    setFormKdvRate('0');
  }

  function resetBatchForm() {
    setBatchDate(todayStr());
    setBatchSupplier('');
    setBatchNote('');
    setBatchLines([{ ...EMPTY_LINE }]);
  }

  function handleSingleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formRestaurant || !formCategory || !formDescription || !formAmount) return;
    addMutation.mutate({
      restaurantId: formRestaurant,
      categoryId: formCategory,
      description: formDescription,
      amount: parseFloat(formAmount),
      kdvRate: parseFloat(formKdvRate) || 0,
      expenseDate: formDate,
      receiptNote: formNote || undefined,
    });
  }

  function handleBatchSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validLines = batchLines.filter(
      (l) => l.categoryId && l.description.trim() && l.amount > 0,
    );
    if (!batchRestaurant || validLines.length === 0) return;
    batchMutation.mutate({
      restaurantId: batchRestaurant,
      receiptDate: batchDate,
      supplier: batchSupplier.trim() || undefined,
      note: batchNote.trim() || undefined,
      items: validLines,
    });
  }

  function updateBatchLine(index: number, field: keyof BatchExpenseItem, value: string | number) {
    setBatchLines((prev) => prev.map((line, i) => (i === index ? { ...line, [field]: value } : line)));
  }

  function removeBatchLine(index: number) {
    setBatchLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  }

  const batchTotal = useMemo(
    () => batchLines.reduce((s, l) => s + (Number(l.amount) || 0), 0),
    [batchLines],
  );

  const batchKdvTotal = useMemo(
    () => batchLines.reduce((s, l) => {
      const amt = Number(l.amount) || 0;
      const rate = Number(l.kdvRate) || 0;
      return s + computeKdv(amt, rate).kdvAmount;
    }, 0),
    [batchLines],
  );

  return (
    <>
      {/* Category Management */}
      <div className="card compact">
        <div className="card-header">
          <p className="card-title">Kategoriler</p>
          <button
            type="button"
            className="button secondary"
            onClick={() => setShowCategoryForm((v) => !v)}
          >
            {showCategoryForm ? 'Kapat' : '+ Yeni Kategori'}
          </button>
        </div>

        {showCategoryForm && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!newCatName.trim()) return;
              addCategoryMutation.mutate({ name: newCatName.trim(), description: newCatDesc.trim() || undefined });
            }}
            className="form-row"
          >
            <div className="form-field">
              <label>Kategori Adı *</label>
              <input
                type="text"
                placeholder="Örneğin: İçecek Malzemeleri"
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                required
              />
            </div>
            <div className="form-field">
              <label>Açıklama</label>
              <input
                type="text"
                placeholder="Opsiyonel"
                value={newCatDesc}
                onChange={(e) => setNewCatDesc(e.target.value)}
              />
            </div>
            <button type="submit" className="button primary" disabled={addCategoryMutation.isPending}>
              {addCategoryMutation.isPending ? 'Ekleniyor...' : 'Ekle'}
            </button>
          </form>
        )}
        {addCategoryMutation.isError && (
          <p className="error-text">
            {addCategoryMutation.error instanceof Error ? addCategoryMutation.error.message : 'Kategori eklenemedi.'}
          </p>
        )}

        <div className="chip-row">
          {categories.map((c) => (
            <span key={c.id} className="chip neutral">
              {c.name}
              <button
                type="button"
                className="text-button danger"
                disabled={deleteCategoryMutation.isPending}
                onClick={() => {
                  if (confirm(`"${c.name}" kategorisini silmek istediğinize emin misiniz?`)) {
                    deleteCategoryMutation.mutate(c.id);
                  }
                }}
              >
                x
              </button>
            </span>
          ))}
          {categories.length === 0 && (
            <span className="helper-text">Henüz kategori yok.</span>
          )}
        </div>
        {deleteCategoryMutation.isError && (
          <p className="error-text">
            {deleteCategoryMutation.error instanceof Error ? deleteCategoryMutation.error.message : 'Kategori silinemedi.'}
          </p>
        )}
      </div>

      {/* Filters */}
      <div className="card compact">
        <div className="card-header">
          <p className="card-title">Filtreler</p>
          <div className="form-row">
            <button
              type="button"
              className="button secondary"
              onClick={() => { setShowForm((v) => !v); setShowBatchForm(false); }}
            >
              {showForm ? 'Kapat' : '+ Tekli Gider'}
            </button>
            <button
              type="button"
              className="button primary"
              onClick={() => { setShowBatchForm((v) => !v); setShowForm(false); }}
            >
              {showBatchForm ? 'Kapat' : '+ Toplu Gider (Fiş)'}
            </button>
          </div>
        </div>
        <div className="form-grid three-columns">
          <div className="form-field">
            <label>Restoran</label>
            <select value={filterRestaurant} onChange={(e) => setFilterRestaurant(e.target.value)}>
              <option value="">Tümü</option>
              {restaurants.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>Kategori</label>
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="">Tümü</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>Ürün / Malzeme Ara</label>
            <input
              type="text"
              placeholder="Örneğin: peynir, salam..."
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Başlangıç Tarihi</label>
            <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} />
          </div>
          <div className="form-field">
            <label>Bitiş Tarihi</label>
            <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Single Add Form */}
      {showForm && (
        <div className="card">
          <p className="card-title">Tekli Gider Ekle</p>
          <form onSubmit={handleSingleSubmit}>
            <div className="form-grid two-columns">
              <div className="form-field">
                <label>Restoran *</label>
                <select value={formRestaurant} onChange={(e) => setFormRestaurant(e.target.value)} required>
                  <option value="">Seçin</option>
                  {restaurants.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Kategori *</label>
                <select value={formCategory} onChange={(e) => setFormCategory(e.target.value)} required>
                  <option value="">Seçin</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Açıklama *</label>
                <input type="text" placeholder="Örneğin: Mozzarella peyniri 5 kg" value={formDescription} onChange={(e) => setFormDescription(e.target.value)} required />
              </div>
              <div className="form-field">
                <label>Tutar KDV Dahil (₺) *</label>
                <input type="number" step="0.01" min="0.01" placeholder="0.00" value={formAmount} onChange={(e) => setFormAmount(e.target.value)} required />
              </div>
              <div className="form-field">
                <label>KDV Oranı (%)</label>
                <select value={formKdvRate} onChange={(e) => setFormKdvRate(e.target.value)}>
                  <option value="0">%0 (KDV Yok)</option>
                  <option value="1">%1</option>
                  <option value="10">%10</option>
                  <option value="20">%20</option>
                </select>
                {parseFloat(formAmount) > 0 && parseFloat(formKdvRate) > 0 && (
                  <span className="helper-text">
                    KDV: {currency(computeKdv(parseFloat(formAmount), parseFloat(formKdvRate)).kdvAmount)}
                    {' · '}Net: {currency(computeKdv(parseFloat(formAmount), parseFloat(formKdvRate)).netAmount)}
                  </span>
                )}
              </div>
              <div className="form-field">
                <label>Tarih *</label>
                <input type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)} required />
              </div>
              <div className="form-field">
                <label>Not</label>
                <input type="text" placeholder="Opsiyonel" value={formNote} onChange={(e) => setFormNote(e.target.value)} />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="button ghost" onClick={() => { setShowForm(false); resetForm(); }}>İptal</button>
              <button type="submit" className="button primary" disabled={addMutation.isPending}>
                {addMutation.isPending ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
            {addMutation.isError && (
              <p className="error-text">
                {addMutation.error instanceof Error ? addMutation.error.message : 'Hata oluştu.'}
              </p>
            )}
          </form>
        </div>
      )}

      {/* Batch Add Form */}
      {showBatchForm && (
        <div className="card">
          <p className="card-title">Toplu Gider Ekle (Fiş / Sipariş)</p>
          <p className="card-subtitle">Bir seferde birden fazla kalem girin. Örneğin marketçi siparişindeki tüm ürünleri tek fişte kaydedin.</p>
          <form onSubmit={handleBatchSubmit}>
            <div className="form-grid two-columns">
              <div className="form-field">
                <label>Restoran *</label>
                <select value={batchRestaurant} onChange={(e) => setBatchRestaurant(e.target.value)} required>
                  <option value="">Seçin</option>
                  {restaurants.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Tarih *</label>
                <input type="date" value={batchDate} onChange={(e) => setBatchDate(e.target.value)} required />
              </div>
              <div className="form-field">
                <label>Tedarikçi / Mağazın</label>
                <input type="text" placeholder="Örneğin: Metro, BİM, Yerel kasap" value={batchSupplier} onChange={(e) => setBatchSupplier(e.target.value)} />
              </div>
              <div className="form-field">
                <label>Fiş Notu</label>
                <input type="text" placeholder="Opsiyonel genel not" value={batchNote} onChange={(e) => setBatchNote(e.target.value)} />
              </div>
            </div>

            <p className="section-divider">Kalemler</p>
            <div className="batch-lines-stack">
              {batchLines.map((line, idx) => (
                <div key={idx} className="batch-line-row">
                  <select
                    value={line.categoryId}
                    onChange={(e) => updateBatchLine(idx, 'categoryId', e.target.value)}
                    required
                    style={{ minWidth: 140 }}
                  >
                    <option value="">Kategori</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Ürün adı (örneğin: Salam 20 kg)"
                    value={line.description}
                    onChange={(e) => updateBatchLine(idx, 'description', e.target.value)}
                    required
                    style={{ flex: 1, minWidth: 180 }}
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="₺ Tutar"
                    value={line.amount || ''}
                    onChange={(e) => updateBatchLine(idx, 'amount', parseFloat(e.target.value) || 0)}
                    required
                    style={{ width: 120 }}
                  />
                  <select
                    value={line.kdvRate}
                    onChange={(e) => updateBatchLine(idx, 'kdvRate', parseFloat(e.target.value) || 0)}
                    style={{ width: 90 }}
                    title="KDV %"
                  >
                    <option value="0">%0</option>
                    <option value="1">%1</option>
                    <option value="10">%10</option>
                    <option value="20">%20</option>
                  </select>
                  <button
                    type="button"
                    className="text-button danger"
                    onClick={() => removeBatchLine(idx)}
                    disabled={batchLines.length <= 1}
                    title="Kalemi sil"
                  >
                    x
                  </button>
                </div>
              ))}
            </div>

            <div className="batch-footer">
              <button
                type="button"
                className="button secondary"
                onClick={() => setBatchLines((prev) => [...prev, { ...EMPTY_LINE }])}
              >
                + Kalem Ekle
              </button>
              <span className="batch-total">
                Toplam: {currency(batchTotal)}
                {batchKdvTotal > 0 && (
                  <span className="muted">
                    (KDV: {currency(batchKdvTotal)})
                  </span>
                )}
              </span>
            </div>

            <div className="form-actions">
              <button type="button" className="button ghost" onClick={() => { setShowBatchForm(false); resetBatchForm(); }}>İptal</button>
              <button type="submit" className="button primary" disabled={batchMutation.isPending}>
                {batchMutation.isPending ? 'Kaydediliyor...' : `${batchLines.filter((l) => l.description.trim()).length} Kalemi Kaydet`}
              </button>
            </div>
            {batchMutation.isError && (
              <p className="error-text">
                {batchMutation.error instanceof Error ? batchMutation.error.message : 'Toplu kayıt sırasında hata oluştu.'}
              </p>
            )}
          </form>
        </div>
      )}

      {/* Summary bar */}
      <div className="insight-grid">
        <div className="insight-card">
          <span>Toplam Gider{filterSearch ? ` ("${filterSearch}")` : ''}</span>
          <strong>{currency(totalFiltered)}</strong>
        </div>
        <div className="insight-card">
          <span>Toplam KDV</span>
          <strong>{currency(totalKdvFiltered)}</strong>
        </div>
        <div className="insight-card">
          <span>Kayıt Sayısı</span>
          <strong>{filtered.length}</strong>
        </div>
      </div>

      {/* View tabs */}
      <div className="tab-switch">
        <button
          type="button"
          className={`tab-button${viewTab === 'all' ? ' active' : ''}`}
          onClick={() => setViewTab('all')}
        >
          Tüm Kayıtlar
        </button>
        <button
          type="button"
          className={`tab-button${viewTab === 'receipts' ? ' active' : ''}`}
          onClick={() => setViewTab('receipts')}
        >
          Fişler / Siparişler ({receipts.length})
        </button>
      </div>

      {/* All-items table */}
      {viewTab === 'all' && (
        <div className="card">
          <p className="card-title">Gider Kayıtları</p>
          {isLoading ? (
            <div className="empty-state"><p>Yükleniyor...</p></div>
          ) : error ? (
            <div className="empty-state">
              <strong>Veriler yüklenemedi</strong>
              <p>{error instanceof Error ? error.message : 'Bilinmeyen hata'}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <strong>Kayıt bulunamadı</strong>
              <p>{filterSearch ? `"${filterSearch}" için sonuç yok. Filtreleri değiştirmeyi deneyin.` : 'Yeni gider eklemek için yukarıdaki butonları kullanın.'}</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>Restoran</th>
                    <th>Kategori</th>
                    <th>Açıklama</th>
                    <th style={{ textAlign: 'right' }}>Tutar</th>
                    <th style={{ textAlign: 'right' }}>KDV</th>
                    <th>Not</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((exp) => (
                    <tr key={exp.id}>
                      <td>{new Date(exp.expenseDate).toLocaleDateString('tr-TR')}</td>
                      <td>{exp.restaurantName}</td>
                      <td><span className="chip neutral">{exp.categoryName}</span></td>
                      <td>{exp.description}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{currency(exp.amount)}</td>
                      <td style={{ textAlign: 'right', fontSize: '0.85rem', color: '#64748b' }}>
                        {exp.kdvRate > 0 ? `%${exp.kdvRate} (${currency(exp.kdvAmount)})` : '—'}
                      </td>
                      <td style={{ fontSize: '0.85rem', color: '#64748b' }}>{exp.receiptNote ?? '—'}</td>
                      <td>
                        <button
                          type="button"
                          className="text-button danger"
                          disabled={deleteMutation.isPending}
                          onClick={() => {
                            if (confirm('Bu gider kaydını silmek istediğinize emin misiniz?')) {
                              deleteMutation.mutate(exp.id);
                            }
                          }}
                        >
                          Sil
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Receipts view */}
      {viewTab === 'receipts' && (
        <div className="card">
          <p className="card-title">Fişler / Toplu Siparişler</p>
          {receiptsLoading ? (
            <div className="empty-state"><p>Yükleniyor...</p></div>
          ) : receipts.length === 0 ? (
            <div className="empty-state">
              <strong>Henüz fiş kaydı yok</strong>
              <p>"+ Toplu Gider (Fiş)" butonu ile sipariş/fiş kaydı oluşturun.</p>
            </div>
          ) : (
            <div className="receipts-list">
              {receipts.map((receipt) => {
                const isExpanded = expandedReceipt === receipt.id;
                return (
                  <div key={receipt.id} className="receipt-card">
                    <div
                      className="receipt-card-header"
                      onClick={() => setExpandedReceipt(isExpanded ? null : receipt.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') setExpandedReceipt(isExpanded ? null : receipt.id); }}
                    >
                      <div className="chip-row" style={{ flex: 1 }}>
                        <strong>{new Date(receipt.receiptDate).toLocaleDateString('tr-TR')}</strong>
                        <span className="chip neutral">{receipt.restaurantName}</span>
                        {receipt.supplier && (
                          <span>{receipt.supplier}</span>
                        )}
                        <span className="helper-text">
                          {receipt.itemCount} kalem
                        </span>
                      </div>
                      <div className="form-row center">
                        <strong>{currency(receipt.totalAmount)}</strong>
                        <span className="helper-text">
                          {isExpanded ? '▲' : '▼'}
                        </span>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="receipt-card-body">
                        {receipt.note && (
                          <p className="helper-text">
                            Not: {receipt.note}
                          </p>
                        )}
                        <div className="table-wrapper">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Kategori</th>
                                <th>Ürün / Açıklama</th>
                                <th style={{ textAlign: 'right' }}>Tutar</th>
                                <th style={{ textAlign: 'right' }}>KDV</th>
                              </tr>
                            </thead>
                            <tbody>
                              {receipt.items.map((item) => (
                                <tr key={item.id}>
                                  <td><span className="chip neutral">{item.categoryName}</span></td>
                                  <td>{item.description}</td>
                                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{currency(item.amount)}</td>
                                  <td style={{ textAlign: 'right', fontSize: '0.85rem', color: '#64748b' }}>
                                    {item.kdvRate > 0 ? `%${item.kdvRate} (${currency(item.kdvAmount)})` : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td colSpan={2} style={{ fontWeight: 600 }}>Toplam</td>
                                <td style={{ textAlign: 'right', fontWeight: 700 }}>{currency(receipt.totalAmount)}</td>
                                <td style={{ textAlign: 'right', fontWeight: 600, color: '#64748b' }}>
                                  {receipt.totalKdv > 0 ? currency(receipt.totalKdv) : '—'}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                        <div className="form-actions">
                          <button
                            type="button"
                            className="text-button danger"
                            disabled={deleteReceiptMutation.isPending}
                            onClick={() => {
                              if (confirm('Bu fişi ve tüm kalemlerini silmek istediğinize emin misiniz?')) {
                                deleteReceiptMutation.mutate(receipt.id);
                              }
                            }}
                          >
                            Fişi Sil
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </>
  );
}
